import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import {outputFromObservable, takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {fromEvent} from 'rxjs';
import {bzIconClose, bzIconExpand, bzIconInfoOutline} from '@bringoz/ui/icons';
import {takeUntil} from 'rxjs/operators';
import * as moment from 'moment';
import * as _ from 'lodash';
import {TimecardDetailStore} from '../../state/timecard-detail.store';
import {TimekeepingAlertsService} from '../../services/timekeeping-alerts.service';
import {TimekeepingDialogsService} from '../../services/timekeeping-dialogs.service';
import {BreadcrumbsService} from '../../../../commons/angular/services/breadcrumbs.service';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {SplitService} from '../../../../commons/services/split-service.service';
import {resolveFieldValue} from '../../../utils/field.util';
import {TimeCardStatusType} from '../../../types/timecard-status.type';
import {
  AddStopDialogData,
  EditAdminTimeDialogData,
  EditEntryDialogData,
  HistoryLogDialogData,
  TimecardOption
} from '../../../types/timekeeping-dialog.type';
import {Address} from '../../../../addresses/models/address.model';
import {LOCKED_ADMIN_TIME_STATUSES} from '../../utils/timecard-detail.mapper';
import {
  TimecardEditEntryEvent,
  TimecardHistoryEvent,
  TimecardShiftEvent,
  TimecardStatusChangeEvent,
  TimecardStopEvent
} from '../timecard-shift-list/timecard-shift-list.component';
import {Stop} from '../../../types/timecard-detail-view.type';
import {getMetersPerLargeDistanceUnit} from '../../../../commons/utils/distance-unit.util';

interface UiRouterStateService {
  go(state: string, params?: Record<string, unknown>, options?: Record<string, unknown>): unknown;

  href(state: string, params?: Record<string, unknown>): string;
}

/**
 * Smart container of the timecard detail (one agent, one day). Owns the store, dialogs and navigation.
 * The AngularJS shell projects the agent select, the map and the timeline into it, and binds them to
 * the context it emits.
 */
require('./timecard-detail.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-detail',
  templateUrl: './timecard-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TimecardDetailStore]
})
export class TimecardDetailComponent implements OnInit, OnDestroy {

  // Route params, parsed by the 'timecardDetail' / 'agent.timecards' state controllers
  readonly agentId = input<string>();
  readonly fromDate = input<number>();
  readonly toDate = input<number>();
  readonly isAgentView = input<boolean>(false);
  readonly listFrom = input<number>();
  readonly listTo = input<number>();
  readonly groupTags = input<string[]>();
  readonly groupIds = input<string[]>();
  readonly branchIds = input<string[]>();
  readonly tags = input<string[]>();
  readonly statuses = input<string[]>();
  readonly displayStatusGroups = input<string[]>();
  readonly queryString = input<string>();
  /** Set by the AngularJS timeline when the user scrubs it. */
  readonly timelineTimestamp = input<number>();

  readonly store = inject(TimecardDetailStore);

  /** Everything the AngularJS map, timeline and agent select need. */
  readonly contextChange = outputFromObservable(toObservable(this.store.shellContext));

  readonly configTimeZone: string;
  readonly siteDistanceUnit: string;
  readonly leftPanelWidth = signal<string>('75%');
  readonly icons = {info: bzIconInfoOutline, close: bzIconClose, fullscreen: bzIconExpand};

  /** Why Add Stop is disabled, if it is. */
  readonly addStopHint = computed(() => {
    if (this.store.isUpdatingEntry()) {
      return 'Update in progress';
    }
    if (!this.store.shifts().length) {
      return 'No shifts available';
    }
    return this.store.editableShift() ? undefined : 'No closed shift available to edit';
  });

  /** Service page links of every stop, resolved once per day load. */
  readonly serviceHrefs = computed(() => {
    const serviceIds = _.uniq(_.flatMap(this.store.shifts(), (shift) => _.flatMap(shift.stops, (stop) => stop.serviceIds || [])));
    return _.fromPairs(serviceIds.map((id) => [id, this.$state.href('service.delivery.info', {id})]));
  });

  private readonly alerts = inject(TimekeepingAlertsService);
  private readonly breadcrumbsService = inject(BreadcrumbsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly $state = injectLegacy<UiRouterStateService>('$state');
  private readonly splitService = injectLegacy<SplitService>('splitService');
  private readonly dialogs = inject(TimekeepingDialogsService);
  private readonly mainContent = viewChild<ElementRef<HTMLElement>>('mainContent');

  constructor() {
    this.configTimeZone = this.splitService.getTimeZone();
    this.siteDistanceUnit = this.splitService.getSiteDistanceLargeUnit();

    effect(() => {
      const timestamp = this.timelineTimestamp();
      if (timestamp) {
        untracked(() => this.store.setTimelineTimestamp(timestamp));
      }
    });
  }

  ngOnInit() {
    this.store.init({
      agentId: this.agentId(),
      fromDate: this.fromDate(),
      toDate: this.toDate(),
      isAgentView: this.isAgentView()
    });
    this.breadcrumbsService.off();
  }

  ngOnDestroy() {
    this.breadcrumbsService.on();
  }

  // ******************************
  //        Navigation
  // ******************************

  onBack() {
    this.$state.go('timekeeping', {
      from: this.listFrom(),
      to: this.listTo(),
      groupTags: this.groupTags(),
      groupIds: this.groupIds(),
      branchIds: this.branchIds(),
      tags: this.tags(),
      statuses: this.statuses(),
      displayStatusGroups: this.displayStatusGroups(),
      query: this.queryString()
    });
  }

  onResizeStart(event: MouseEvent) {
    event.preventDefault();
    const container = this.mainContent()?.nativeElement;
    if (!container) {
      return;
    }
    const mouseUp$ = fromEvent(document, 'mouseup');
    fromEvent<MouseEvent>(document, 'mousemove')
      .pipe(takeUntil(mouseUp$), takeUntilDestroyed(this.destroyRef))
      .subscribe((move) => {
        const x = move.clientX - container.getBoundingClientRect().left;
        this.leftPanelWidth.set(`${Math.max(0, x)}px`);
      });
  }

  // ******************************
  //        Status & refresh
  // ******************************

  onChangeStatus({shift, status}: TimecardStatusChangeEvent) {
    const analysis = this.store.analysisMap()[shift.id];
    const isApproval = status === TimeCardStatusType.Approved || status === TimeCardStatusType.Completed;
    if (analysis?.hasInfeasibleRisk && this.store.blockApprovalOnInfeasible() && isApproval) {
      this.alerts.updateError('TIMECARD_INFEASIBLE_BLOCK');
      return;
    }

    if (status === TimeCardStatusType.Archived) {
      this.dialogs.confirm('TIMECARD_ARCHIVE_CONFIRM_TITLE', 'TIMECARD_ARCHIVE_CONFIRM_MESSAGE', true)
        .then((confirmed) => confirmed && this.store.changeStatus(shift.id, status));
      return;
    }
    this.store.changeStatus(shift.id, status);
  }

  onRefreshData({shift}: TimecardShiftEvent) {
    this.store.refreshData(shift.id);
  }

  // ******************************
  //        Edits
  // ******************************

  onEditEntry({stop, shift, field, anchor}: TimecardEditEntryEvent) {
    if (!stop.timecardId || stop.entryIndex === undefined) {
      console.error('Missing timecard reference for stop:', stop);
      return;
    }

    const metersPerUnit = getMetersPerLargeDistanceUnit(this.splitService.isImperial());
    const dialogData: EditEntryDialogData = {
      stopName: stop.name,
      arrival: stop.arrival,
      departure: stop.departure,
      serviceTime: stop.serviceTime,
      plannedServiceTime: stop.plannedServiceTime,
      passiveTrackingServiceTime: stop.passiveTrackingServiceTime,
      editMode: field,
      initialArrival: stop.initialArrival,
      initialDeparture: stop.initialDeparture,
      // No zoneId: the grid shows the time in the user's selected timezone, so the editor reads and writes
      // it in that same timezone.
      clockInTime: shift?.clockInTime,
      clockOutTime: shift?.clockOutTime,
      distance: _.isNil(stop.miles) ? undefined : _.round(stop.miles / metersPerUnit, 2),
      siteDistanceUnit: this.siteDistanceUnit
    };

    this.dialogs.editEntry(anchor, dialogData)
      .then((result) => {
        if (!result) return;
        // Distance is edited in the site unit and stored in meters
        const value = field === 'arrival' ? result.arrival :
          field === 'departure' ? result.departure :
            field === 'distance' ? (_.isNil(result.distance) ? null : Math.round(result.distance * metersPerUnit)) :
              result.serviceTime;
        if (!_.isNil(value)) {
          this.store.updateEntry(stop, field, value);
        }
      });
  }

  onEditAdminTime() {
    if (!this.store.canEditAdminTime()) {
      return;
    }

    const agentName = this.store.employee().name;
    const fromDate = this.store.fromDate();
    const dayLabel = fromDate ? moment(fromDate).format(this.splitService.getDateAndTimeConfig().date || 'MM/DD/YYYY') : '';

    const dialogData: EditAdminTimeDialogData = {
      title: agentName && dayLabel ? `${agentName} — ${dayLabel}` : (agentName || ''),
      shifts: this.store.shifts().map((shift) => ({
        timeCardId: shift.id,
        shiftName: shift.shiftName,
        adminTime: this.store.findTimecard(shift.id)?.adminTime || 0,
        isLocked: _.includes(LOCKED_ADMIN_TIME_STATUSES, shift.status)
      })),
      onOpenHistory: (timeCardId: string) => this.openHistoryLog({
        timeCardId,
        filterField: 'adminTime',
        filterStopName: _.find(this.store.shifts(), {id: timeCardId})?.shiftName
      })
    };

    this.dialogs.editAdminTime(dialogData)
      .then((result) => {
        if (result?.changes?.length) {
          this.store.updateAdminTime(result.changes);
        }
      });
  }

  onAddStop() {
    const shift = this.store.editableShift();
    if (!shift?.isEditable) {
      return;
    }

    // The dialog lists only the non-archived entries; their indexes in the full list are kept to map
    // the dialog's "existing entry" back onto the entry that is actually edited.
    const entryIndexesByTimecard: Record<string, number[]> = {};
    const timecards: TimecardOption[] = this.store.shifts()
      .filter((item) => item.isEditable)
      .map((item) => {
        const timecard = this.store.findTimecard(item.id);
        const entries = timecard?.reportedData?.entries || [];
        const activeIndexes = _.filter(_.range(entries.length), (index) => !entries[index].archive);
        entryIndexesByTimecard[item.id] = activeIndexes;
        return {
          id: item.id,
          shiftNumber: item.shiftNumber,
          shiftName: item.shiftName,
          clockInTime: item.clockInTime,
          clockOutTime: item.clockOutTime,
          status: item.status,
          entryTypes: _.uniq(_.compact(entries.map((entry) => entry.type))),
          breaks: (resolveFieldValue(timecard?.breaksField) || []).map((item) => ({id: item.id, breakTypeId: item.breakTypeId})),
          entries: activeIndexes.map((index) => ({
            accountId: entries[index].accountId,
            startTime: resolveFieldValue(entries[index].startTime),
            endTime: resolveFieldValue(entries[index].endTime),
            type: entries[index].type,
            archive: entries[index].archive
          }))
        } as TimecardOption;
      });

    this.store.loadAgentAddress()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((agentAddress: Address | null) => {
        const dialogData: AddStopDialogData = {
          referenceDate: shift.clockInTime || this.store.fromDate(),
          // Read live - the user may have switched timezone since the screen opened.
          zoneId: this.splitService.getTimeZone(),
          timecards,
          selectedTimecardId: shift.id,
          agentAddress,
          agentId: this.store.group()?.agentId || this.agentId()
        };

        this.dialogs.addStop(dialogData)
          .then((result) => {
            if (!result) return;
            this.store.addStop(result.isUpdateExisting && !_.isNil(result.existingEntryIndex)
              ? {...result, existingEntryIndex: entryIndexesByTimecard[result.timecardId]?.[result.existingEntryIndex]}
              : result);
          });
      });
  }

  onArchiveStop({stop}: TimecardStopEvent) {
    this.dialogs.confirm('Archive Stop', 'This stop will be archived.', true)
      .then((confirmed) => confirmed && this.store.archiveStop(stop));
  }

  // ******************************
  //        History
  // ******************************

  onOpenHistory({stop, shift, field}: TimecardHistoryEvent) {
    this.openHistoryLog({
      timeCardId: stop.timecardId || shift.id,
      filterField: field,
      filterEntryId: stop.id,
      filterStopName: stop.name,
      filterEntryIndex: stop.entryIndex + 1,
      isSensitiveLocationEntry: !!stop.isSensitiveLocationEntry
    });
  }

  onOpenStatusHistory({shift}: TimecardShiftEvent) {
    this.openHistoryLog({timeCardId: shift.id, filterField: 'status'});
  }

  onSelectStop({stop, shiftId}: { stop: Stop; shiftId: string }) {
    this.store.selectStop(stop, shiftId);
  }

  private openHistoryLog(data: HistoryLogDialogData) {
    if (!data.timeCardId) {
      return;
    }
    this.store.loadFullHistory(data.timeCardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (historyData) => this.dialogs.historyLog({...data, historyData}),
        error: (error) => {
          console.error('Failed to load timecard history:', error);
          this.alerts.updateError();
        }
      });
  }
}
